import React from 'react'
import PropTypes from 'prop-types'
import { differenceInDays, formatDistanceToNowStrict } from 'date-fns'
import isValid from 'date-fns/isValid'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import ListingRemovalMessage from 'Components/ListingRemovalMessage'
import { BlurCont } from 'Layout'
import {
  _ListingDetailContainer,
  _Location,
  _Status,
  _Date,
  _AllHiddenMessage,
  _UnitInformationContainer,
  _ArchiveListingInfoDesktop,
  _ArchiveListingInfoMobile,
  _DateContainer,
  _DateDistance,
  _StatusContainer,
  _ListedInfo,
  _DaysOnMarket,
  _DaysOnMarketContainer,
  _PricePerSqft,
} from './styles'

const ArchiveCardDetail = (
  {
    suiteNumber,
    listingAddress,
    statusText,
    askingPrice,
    closedPrice,
    endDate,
    beds,
    baths,
    parkings,
    sqft,
    sqftMax,
    sqftMin,
    // forMobile,
    isPrint,
    isHiddenOnPrint,
    hiddenFields,
    handleContactAgentClick,
    entryDate,
    userIsOneOfAgentAdminSupport,
    pricePerSqft,
    listingPage,
    userIsAuthentificated,
    userEmailVerified,
    showLoginModal,
  }) => {
  const listingIsLocked = !userIsAuthentificated || !userEmailVerified
  const renderEndDate = (date) => {
    if(!isValid(parseISO(date))) return date
    const days = differenceInDays(new Date(), parseISO(date))
    let dateDistance
    if(days > 90) dateDistance = formatDistanceToNowStrict(parseISO(date))
    else dateDistance = formatDistanceToNowStrict(parseISO(date), { unit: 'day' })
    return (
      <>
        <_Date>
          {date ? format(parseISO(date), 'MMM dd, yyyy') : ''}
        </_Date>
        <_DateDistance>
          {date ? `${dateDistance} ago` : ''}
        </_DateDistance>
      </>
    )
  }
  const renderStatus = (status) => {
    const soldOrLeased = status === 'Sold' || status === 'Leased'
    const listingStatusText = soldOrLeased ? `${status} for` : status
    const priceSqftText = userIsOneOfAgentAdminSupport && pricePerSqft ? ` (${pricePerSqft}/sqft)` : ''
    const underline = status === 'Login Required'
    return (
      <_Status underline={underline}>
        <span>{listingStatusText}&nbsp;</span>
        {soldOrLeased && (
          <BlurCont blur={listingIsLocked}>{closedPrice || 'NA'}&nbsp;</BlurCont>)}
        {priceSqftText && <_PricePerSqft>{priceSqftText}</_PricePerSqft>}
      </_Status>
    )
  }

  const renderListedDateInfo = () => {
    if(!isValid(parseISO(entryDate))) return entryDate

    return (!!entryDate && !!askingPrice) ? (
      <_ListedInfo>
        Listed for &nbsp;<BlurCont blur={listingIsLocked}>{askingPrice}</BlurCont>
        &nbsp;on&nbsp;<BlurCont blur={listingIsLocked}>{format(parseISO(entryDate), 'MMM dd, yyyy')}</BlurCont>
      </_ListedInfo>
    ) : (
      <_ListedInfo>Not Available</_ListedInfo>
    )
  }

  const calcDaysOnMarket = () => {
    if(!isValid(parseISO(entryDate)) || !isValid(parseISO(endDate))) {
      return (
        <_DaysOnMarket>Not Available</_DaysOnMarket>
      )
    }
    const daysOnMarket = differenceInDays(parseISO(endDate), parseISO(entryDate))
    return (
      <_DaysOnMarket>
        <BlurCont blur={listingIsLocked}>{daysOnMarket}</BlurCont>
        <span>&nbsp;days on market</span>
      </_DaysOnMarket>

    )
  }
  const listingHiddenFields = hiddenFields || {}
  return (
    <_ListingDetailContainer
      isHiddenOnPrint={isHiddenOnPrint}
      forMobile={false}
      listingPage={listingPage}
      onClick={userIsAuthentificated ? undefined : showLoginModal}
    >
      {
        !listingPage && !listingHiddenFields.listing_info &&
          <_UnitInformationContainer>
            {!listingHiddenFields.listing_info && (suiteNumber ? (
              <_Location>
                Unit {suiteNumber}
              </_Location>
            ) : (
              <_Location>{listingAddress}</_Location>
            ))}
            {
              !listingHiddenFields.listing_info && (
                <_ArchiveListingInfoDesktop
                  beds={beds}
                  baths={baths}
                  parkings={parkings}
                  sqft={sqft}
                  sqftMax={sqftMax}
                  sqftMin={sqftMin}
                  isPrint={isPrint}
                  listingIsLocked={listingIsLocked}
                />
              )
            }
          </_UnitInformationContainer>
      }
      {
        // error message need to be aligned with listedPeriod
        listingHiddenFields.listing_info || listingHiddenFields.prices ? (
          <_AllHiddenMessage>
            <ListingRemovalMessage
              hideContactAgentText={userIsOneOfAgentAdminSupport}
              hiddenFields={hiddenFields}
              onContactAgentClick={handleContactAgentClick}
            />
          </_AllHiddenMessage>
        ) : (
          <>
            <_DateContainer listingPage={listingPage}>
              <BlurCont blur={listingIsLocked}>{renderEndDate(endDate)}</BlurCont>
            </_DateContainer>
            <_StatusContainer listingPage={listingPage}>
              {renderStatus(statusText)}
              {renderListedDateInfo()}
              {calcDaysOnMarket()}
            </_StatusContainer>
            <_DaysOnMarketContainer listingPage={listingPage}>
              {calcDaysOnMarket()}
            </_DaysOnMarketContainer>
            {
              !listingPage && (
                <_ArchiveListingInfoMobile
                  beds={beds}
                  baths={baths}
                  parkings={parkings}
                  sqft={sqft}
                  sqftMax={sqftMax}
                  sqftMin={sqftMin}
                  isPrint={isPrint}
                  forMobile
                  listingIsLocked={listingIsLocked}
                />
              )
            }
          </>
        )}
    </_ListingDetailContainer>
  )
}

ArchiveCardDetail.propTypes = {
  askingPrice: PropTypes.string,
  closedPrice: PropTypes.string,
  beds: PropTypes.string,
  baths: PropTypes.number,
  parkings: PropTypes.number,
  sqft: PropTypes.number,
  endDate: PropTypes.string,
  entryDate: PropTypes.string,
  suiteNumber: PropTypes.string,
  isPrint: PropTypes.bool,
  sqftMax: PropTypes.number,
  sqftMin: PropTypes.number,
  // forMobile: PropTypes.bool,
  statusText: PropTypes.string,
  listingAddress: PropTypes.string,
  isHiddenOnPrint: PropTypes.bool,
  userIsOneOfAgentAdminSupport: PropTypes.bool,
  hiddenFields: PropTypes.shape({
    photos: PropTypes.bool,
    prices: PropTypes.bool,
    listing_info: PropTypes.bool,
  }),
  handleContactAgentClick: PropTypes.func,
  pricePerSqft: PropTypes.string,
  listingPage: PropTypes.bool,
  userEmailVerified: PropTypes.bool,
  userIsAuthentificated: PropTypes.bool,
  showLoginModal: PropTypes.func,
}

export default ArchiveCardDetail

